// Z86: allocate (part of) a supplier invoice's NET amount to existing cost lines on one or more jobs.
// Flow: pick job -> pick that job's cost line -> enter amount. Warnings are re-computed live by the
// database function and must each be ticked before saving; the server re-checks on save.
import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { supabase } from '../../api/supabase'
import SearchableSelect from '../SearchableSelect'
import WarningAckList from './WarningAckList'
import {
  allAcked, allocatedNet, detectWarnings, toAckInputs, fetchCostLines, r2, saveAllocations, searchJobs,
  type AckState, type AllocInput, type CostLine, type Decision, type DecisionInput, type SupplierInvoice, type Warning,
} from '../../lib/supplierInvoices'

interface Row { id: number; job: { id: string; label: string } | null; lines: CostLine[] | null; chargeId: string; amount: string; notes: string }
interface Props { invoice: SupplierInvoice; onClose: () => void; onSaved: () => void }

let rowSeq = 1
const newRow = (): Row => ({ id: rowSeq++, job: null, lines: null, chargeId: '', amount: '', notes: '' })

export default function AllocationDialog({ invoice, onClose, onSaved }: Props) {
  const [rows, setRows] = useState<Row[]>([newRow()])
  const [codes, setCodes] = useState<Record<string, string>>({})
  const [warnings, setWarnings] = useState<Warning[]>([])
  const [decisions, setDecisions] = useState<Decision[]>([])
  const [acks, setAcks] = useState<AckState>({})
  const [choice, setChoice] = useState<Record<string, 'replace' | 'keep'>>({})
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const alreadyAllocated = allocatedNet(invoice)

  useEffect(() => {
    supabase.from('charge_codes').select('id, code').then(({ data }) =>
      setCodes(Object.fromEntries((data ?? []).map((c: { id: string; code: string }) => [c.id, c.code]))))
  }, [])

  const allocs: AllocInput[] = useMemo(() => rows
    .filter(r => r.chargeId && parseFloat(r.amount) > 0)
    .map(r => ({ job_charge_id: r.chargeId, amount: parseFloat(r.amount), notes: r.notes || undefined })), [rows])
  const batch = r2(allocs.reduce((s, a) => s + a.amount, 0))
  const unallocatedAfter = r2(invoice.net_amount - alreadyAllocated - batch)
  const over = unallocatedAfter < -0.005

  // Live detection (debounced). A stable signature avoids re-querying on unrelated renders.
  const sig = JSON.stringify(allocs)
  const sigRef = useRef('')
  const jobLabels = useRef<Record<string, string>>({})   // job id -> job_ref, filled by the picker's search results
  useEffect(() => {
    sigRef.current = sig
    if (allocs.length === 0) { setWarnings([]); setDecisions([]); return }
    const t = setTimeout(() => {
      detectWarnings({ supplierId: invoice.supplier_id, invoiceNo: null, currency: invoice.currency, exchRate: invoice.exch_rate, invoiceId: invoice.id, allocs })
        .then(r => { if (sigRef.current === sig) { setWarnings(r.warnings); setDecisions(r.decisions) } })
        .catch(e => setError(e instanceof Error ? e.message : String(e)))
    }, 400)
    return () => clearTimeout(t)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sig, invoice.id])

  const update = (id: number, patch: Partial<Row>) => setRows(rs => rs.map(r => (r.id === id ? { ...r, ...patch } : r)))

  const pickJob = useCallback(async (rowId: number, jobId: string, label: string) => {
    update(rowId, { job: { id: jobId, label }, lines: null, chargeId: '' })
    try { update(rowId, { lines: await fetchCostLines(jobId) }) }
    catch (e) { setError(e instanceof Error ? e.message : String(e)) }
  }, [])

  const lineText = (l: CostLine) =>
    `${(l.charge_code_id && codes[l.charge_code_id]) || '—'} · ${l.description || 'no description'} · est ${l.est_amount ?? '—'} ${l.cost_currency} · actual ${l.act_amount ?? '—'} · ${l.cost_supplier?.name || 'Supplier TBC'}`
  const lineLabel = (chargeId: string) => {
    const r = rows.find(x => x.chargeId === chargeId); const l = r?.lines?.find(x => x.id === chargeId)
    return l && r?.job ? `${r.job.label}: ${(l.charge_code_id && codes[l.charge_code_id]) || l.description || 'line'}` : undefined
  }

  const needsDecision = decisions.filter(d => !choice[d.job_charge_id])
  const canSave = allocs.length > 0 && !over && allAcked(warnings, acks) && needsDecision.length === 0 && !saving

  async function handleSave() {
    setSaving(true); setError(null)
    try {
      const dec: DecisionInput[] = decisions.map(d => ({ job_charge_id: d.job_charge_id, action: choice[d.job_charge_id] }))
      const res = await saveAllocations(invoice.id, allocs, toAckInputs(warnings, acks), dec)
      if (res.ok) { onSaved(); return }
      if (res.warnings) { setWarnings(w => [...w.filter(x => !res.warnings!.some(n => n.key === x.key)), ...res.warnings!]); setError('New warnings appeared (someone else may have just allocated). Review and tick them, then save again.') }
      else if (res.decisions) { setDecisions(res.decisions); setError('A hand-entered actual cost needs a decision before saving.') }
      else setError(res.message || res.error)
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e))
    } finally {
      setSaving(false)
    }
  }

  const input = 'rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 px-2 py-1.5 text-sm text-gray-900 dark:text-white'
  return (
    <div className="fixed inset-0 z-[60] flex items-center justify-center bg-black/50 p-4" role="dialog" aria-modal="true">
      <div className="flex max-h-[92vh] w-full max-w-4xl flex-col rounded-xl bg-white shadow-xl dark:bg-gray-800">
        <div className="border-b border-gray-200 px-6 py-4 dark:border-gray-700">
          <h2 className="text-lg font-semibold text-gray-900 dark:text-white">Allocate invoice {invoice.invoice_no}</h2>
          <p className={`text-sm ${over ? 'font-semibold text-red-600' : 'text-gray-600 dark:text-gray-300'}`}>
            Allocated {r2(alreadyAllocated + batch).toFixed(2)} / Unallocated {unallocatedAfter.toFixed(2)} {invoice.currency}
            <span className="text-xs"> (both net of VAT; invoice net {invoice.net_amount.toFixed(2)})</span>
          </p>
        </div>

        <div className="space-y-3 overflow-y-auto px-6 py-4">
          {rows.map(r => (
            <div key={r.id} className="rounded-lg border border-gray-200 p-3 dark:border-gray-700">
              <div className="grid gap-2 md:grid-cols-[1fr_2fr_9rem_auto]">
                <SearchableSelect
                  options={[]} value={r.job?.id ?? ''} selectedOption={r.job}
                  onChange={v => { if (v) pickJob(r.id, v, jobLabels.current[v] ?? v) }}
                  onSearch={async term => {
                    const found = (await searchJobs(term)).map(j => ({ id: j.id, label: j.job_ref }))
                    found.forEach(j => { jobLabels.current[j.id] = j.label })
                    return found
                  }}
                  placeholder="Job ref…"
                />
                <select className={input} value={r.chargeId} disabled={!r.lines} onChange={e => update(r.id, { chargeId: e.target.value })}>
                  <option value="">{r.job ? (r.lines ? '— cost line —' : 'Loading…') : 'Pick a job first'}</option>
                  {r.lines?.map(l => <option key={l.id} value={l.id}>{lineText(l)}</option>)}
                </select>
                <input type="number" step="0.01" min="0" placeholder={`Net ${invoice.currency}`} className={input}
                  value={r.amount} onChange={e => update(r.id, { amount: e.target.value })} />
                <button className="px-2 text-gray-400 hover:text-red-600" title="Remove row"
                  onClick={() => setRows(rs => (rs.length > 1 ? rs.filter(x => x.id !== r.id) : [newRow()]))}>✕</button>
              </div>
              {r.lines && r.lines.length === 0 && (
                <p className="mt-2 text-xs text-amber-700 dark:text-amber-300">
                  This job has no cost lines. Add one on the job&apos;s Charges tab first (tick &ldquo;Include in Cost&rdquo;) &mdash; allocation never creates lines for you.
                </p>
              )}
            </div>
          ))}
          <button className="text-sm text-blue-600 hover:underline dark:text-blue-400" onClick={() => setRows(rs => [...rs, newRow()])}>+ Add another line</button>

          {decisions.map(d => (
            <div key={d.job_charge_id} className="rounded-lg border border-blue-300 bg-blue-50 p-3 text-sm text-blue-900 dark:border-blue-700 dark:bg-blue-900/20 dark:text-blue-100">
              <p className="font-medium">{lineLabel(d.job_charge_id) ?? 'A cost line'} already has a hand-entered actual cost of {d.manual_amount} {d.currency}.</p>
              <label className="mr-4 mt-1 inline-flex items-center gap-1"><input type="radio" name={`dec-${d.job_charge_id}`} checked={choice[d.job_charge_id] === 'replace'} onChange={() => setChoice(c => ({ ...c, [d.job_charge_id]: 'replace' }))} /> Replace it with the invoice figure</label>
              <label className="inline-flex items-center gap-1"><input type="radio" name={`dec-${d.job_charge_id}`} checked={choice[d.job_charge_id] === 'keep'} onChange={() => setChoice(c => ({ ...c, [d.job_charge_id]: 'keep' }))} /> It is a different cost &mdash; keep it and add this invoice on top</label>
            </div>
          ))}

          <WarningAckList warnings={warnings} state={acks} onChange={setAcks} lineLabel={lineLabel} />
          {over && <p className="text-sm font-medium text-red-600">Allocations exceed the invoice net amount.</p>}
          {error && <div className="rounded-lg border border-red-300 bg-red-50 p-3 text-sm text-red-700 dark:border-red-800 dark:bg-red-900/20 dark:text-red-300">{error}</div>}
        </div>

        <div className="flex justify-end gap-2 border-t border-gray-200 px-6 py-3 dark:border-gray-700">
          <button onClick={onClose} className="rounded-lg border border-gray-300 px-4 py-2 text-sm text-gray-700 dark:border-gray-600 dark:text-gray-200">Cancel</button>
          <button onClick={handleSave} disabled={!canSave} className="rounded-lg bg-blue-600 px-4 py-2 text-sm font-medium text-white hover:bg-blue-700 disabled:opacity-50">
            {saving ? 'Saving…' : warnings.length > 0 ? 'Confirm and save' : 'Save allocations'}
          </button>
        </div>
      </div>
    </div>
  )
}
